import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DELETE_LIST, VIEW_AUDIT } from 'src/app/constants/action-constants';
import {
  CONTACT_LIST_SEARCH_CONTAINER,
  FACILITY_DETAIL_CONTAINER,
} from 'src/app/constants/common.constants';
import { CONTACT_LIST_TAB_PREFIX } from 'src/app/constants/contact-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import {
  dynamicSort,
  isNullOrEmptyString,
} from 'src/app/core/functions/common-functions';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ContactBuilderService } from 'src/app/services/builder/contact-builder.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ContactListTableService } from 'src/app/services/table/contact-list-table.service';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ContactListSimpleViewContainerComponent } from '../contact-list-simple-view/contact-list-simple-view-container.component';
import { ScheduleFormComponent } from '../../components/schedule-form/schedule-form.component';
import { Schedule } from 'src/app/model/contacts/contact-list-schedule';
import {
  CalendarOptions,
  DateClickInfo,
  DateSelectInfo,
  EventClickInfo,
} from 'fullcalendar';
import dayGridPlugin from '@fullcalendar/angular/daygrid';
import interactionPlugin from '@fullcalendar/angular/interaction';
import classicThemePlugin from '@fullcalendar/angular/themes/classic';
import {
  getDateInTimeZone,
  isNullOrUndefined,
  sortByProperty,
} from 'src/app/utils/utils';
import { AuthService } from 'src/app/services/auth.service';
import moment from 'moment';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { Facility } from 'src/app/model/locations/facility';
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { FacilityContactListType } from 'src/app/services/api/facility-api.service';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@openng/optimus-ui/tabs';
import { Ripple } from '@openng/optimus-ui/ripple';
import { Panel } from '@openng/optimus-ui/panel';
import { RadioButton } from '@openng/optimus-ui/radiobutton';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { ContactListFacilitiesComponent } from '../../components/contact-list-facilities/contact-list-facilities.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import { ScheduleImportComponent } from '../../components/schedule-import/schedule-import.component';
import { TranslatePipe } from '@ngx-translate/core';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-contact-list-detail-view-container',
  templateUrl: './contact-list-detail-view-container.component.html',
  styleUrls: ['./contact-list-detail-view-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    Select,
    FormsModule,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    ContactListSimpleViewContainerComponent,
    Panel,
    ReactiveFormsModule,
    AutoComplete,
    RadioButton,
    ButtonDirective,
    InputText,
    ContactListFacilitiesComponent,
    FullCalendarModule,
    ScheduleFormComponent,
    ScheduleImportComponent,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class ContactListDetailViewContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _contactListTable = inject(ContactListTableService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _authApi = inject(AuthApiService);
  protected _auth = inject(AuthService);
  protected _contactBuilder = inject(ContactBuilderService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _dialogService = inject(DialogService);

  readonly view = viewChild<ContactListSimpleViewContainerComponent>('sview');
  readonly scheduleForm =
    viewChild.required<ScheduleFormComponent>('scheduleForm');

  DirtyStatus = DirtyStatus;
  readonly contactList = signal<ContactList | null>(null);
  memberContactArray: Contact[] = [];
  loaded = false;
  facilities: any[] = [];
  filteredFacilities: any[] = [];
  facilityFilterText = signal<string | null>(null);
  contactListFacilities = computed(() => {
    const filterText = this.facilityFilterText()?.toLowerCase();
    let resultFacilities = this.contactList()?.facilities ?? [];

    if (filterText) {
      resultFacilities = resultFacilities.filter((facility) =>
        facility.facilityName?.toLowerCase().includes(filterText)
      );
    }

    return resultFacilities;
  });
  clFacilitiesHeader = signal<string>('');
  managementFacilities = computed(() => {
    const filterText = this.facilityFilterText()?.toLowerCase();
    let resultFacilities = this.contactList()?.ManagementFacilities ?? [];

    if (filterText) {
      resultFacilities = resultFacilities.filter((facility) =>
        facility.facilityName?.toLowerCase().includes(filterText)
      );
    }

    return resultFacilities;
  });
  managementFacilitiesHeader = signal<string>('');
  additionalResourceListFacilities = computed(() => {
    const filterText = this.facilityFilterText()?.toLowerCase();
    let resultFacilities =
      this.contactList()?.AdditionalResourceListFacilities ?? [];

    if (filterText) {
      resultFacilities = resultFacilities.filter((facility) =>
        facility.facilityName?.toLowerCase().includes(filterText)
      );
    }

    return resultFacilities;
  });
  additionalResourceListFacilitiesHeader = signal<string>('');

  addToFacilityForm!: UntypedFormGroup;
  saving = false;

  backToSearchResult!: BreadCrumb;
  listBreadCrumb!: BreadCrumb;

  canDelete = false;
  canCreate = false;
  canSchedule = false;
  canViewUpdaters = false;
  allContacts: Contact[] = [];
  filteredContacts: any[] = [];
  selectedUpdater: any;

  actions: SelectItem[] = [];
  selectedAction: any;
  locations: any[] = [];
  tabIndex = 0;

  breadCrumbSub!: Subscription;
  deleteEntryMsg!: string;
  deleteEntryHeader!: string;
  okBtnLabel!: string;
  cancelBtnLabel!: string;
  deleteHeader!: string;

  scheduleFormVisible: boolean = false;
  scheduleFormType: string = 'Create';
  disableDelete: boolean = false;
  contactListSchedule: Schedule[] = [];
  selectedSchedule: any;
  today = new Date();
  events: any[] = [];

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    headerToolbar: {
      start: 'prev,next',
      center: 'title',
      end: 'today',
    },
    aspectRatio: 3,
    selectable: true,
    plugins: [classicThemePlugin, dayGridPlugin, interactionPlugin],
    dateClick: this.handleDateClick.bind(this),
    eventClick: this.handleEventClick.bind(this),
    select: this.handleSelectable.bind(this),
  };

  constructor() {
    super();
  }
  ngOnInit(): void {
    this.setupTabClosingSubscription();
    this.setupBreadCrumbSub();
    this.initialize();

    this._contactApi
      .getContacts()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.allContacts = response.sort(
            dynamicSort('firstName', 1)
          ) as Contact[];
        },
      });

    this.setupFacilityHeaders();
  }

  ngOnDestroy(): void {
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }
    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(this.listBreadCrumb);
          const tab = this._tabService.buildNewTab(
            CONTACT_LIST_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        }
      );
  }

  initialize(): void {
    const id = this.id;
    this.contactList.set(this._contactListTable.getSelected());
    this.memberContactArray = this.contactList()?.members.map(
      (member) => member.contact!
    )!;
    const cList = this.contactList();
    if (cList) {
      this.loadScreenData();
    } else if (id) {
      // use network query to find contact list by id.
      this._contactListApi
        .getContactList(id)
        .pipe(take(1))
        .subscribe({
          next: (list) => {
            this.contactList.set(list);
            this.memberContactArray = this.contactList()?.members.map(
              (member) => member.contact!
            )!;
            this.doCheckComplete = false;
            this.loadScreenData();
            this.loading = false;
          },
          error: (error) => {
            this.loading = false;
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.RETRIEVE_ERROR',
              bodyKey: 'COMMON.MESSAGES.ERROR.RETRIEVE_ERROR_MESSAGE',
            });
          },
        });
    } else {
      // Close this tab since the list is invalid. This will set the active
      // tab to the tab the user was on previously.
      this._logAndMessage.errorLogOnly('Contact List: ID from route was null');
      if (this._deviceService.isMobile()) {
        const tab = this._tabService.buildNewTab(
          CONTACT_LIST_SEARCH_CONTAINER,
          true
        );
        this._tabService.setMobileTab(tab);
      } else {
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
    }
  }

  async loadScreenData(): Promise<void> {
    if (this._deviceService.isMobile()) {
      this._translateService
        .get('CONTACT.SCREEN.CONTACT_LIST')
        .subscribe((label) => {
          this._breadCrumbService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, true)
          );
          if (this.contactList) {
            this.listBreadCrumb = new BreadCrumb(
              this.contactList()!.name!,
              null,
              false
            );
            this._breadCrumbService.addBreadCrumb(this.listBreadCrumb);
          }
        });
    } else {
      const label = await firstValueFrom(
        this._translateService.get('CONTACT.SCREEN.CONTACT_LIST')
      );
      const header = label + ': ' + this.contactList()!.name;
      const tab = this._tabService.getTabById(this.contactList()!.id);
      this._tabService.updateTabLabel(tab!, header);
    }

    this.loadDropDownData();
    this.loadCalendarEvents();

    this.findUpdaters();
    this.loadFacilities();

    this.addToFacilityForm = this._fb.group({
      state: [null],
      county: [null],
      township: [null],
      facility: [null, Validators.required],
      facilityContactListType: ['contactList'],
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.CREATE_CONTACT_LISTS,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.DELETE_CONTACT_LIST,
    ]);
    this.canViewUpdaters = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.VIEW_CONTACT_LIST_UPDATERS,
    ]);

    this.updateActionsList();

    if (
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTACTS.CONTACT_LISTS.SCHEDULE_CONTACT_LISTS,
      ])
    ) {
      this.canSchedule = true;
    } else {
      this._contactApi
        .getContactByEmail(this._auth.getEmail()!)
        .pipe(take(1))
        .subscribe((data: Contact) => {
          const id = data.id;
          const onList = this.contactList()!.members.some(
            (m) => m.contactId === id
          );
          const anUpdater = this.contactList()!.updaters.some(
            (u) => u.contactId === id
          );
          if (!this.canSchedule) {
            this.canSchedule =
              (onList &&
                this._authApi.doesUserHaveAllClaimsFromList([
                  CLAIMS.CONTACTS.CONTACT_LISTS.SCHEDULE_OWN_CONTACT_LISTS,
                ])) ||
              anUpdater;
          }
        });

      if (
        this._authApi.doesUserHaveAllClaimsFromList([
          CLAIMS.CONTACTS.CONTACT_LISTS.SCHEDULE_DIRECT_REPORTS_CONTACT_LISTS,
        ])
      ) {
        this._contactApi
          .queryForWhoReportsToMe(this._auth.getEmail()!)
          .pipe(take(1))
          .subscribe((users) => {
            const reportingIds = users.map((report: any) => report.id);
            const reportOnList = this.contactList()!.members.some((m) =>
              reportingIds.includes(m.contactId)
            );
            if (reportOnList) {
              this.canSchedule = true;
            }
          });
      }
    }
  }

  findUpdaters(): void {
    const contactIdArray = this.contactList()!.updaters.map((m) => m.contactId);
    let contactArray = [];
    this._contactApi
      .getContacts()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const clone = [...response];
          const innerArray = clone.filter(
            (c) => contactIdArray.indexOf(c!.id) > -1
          );
          contactArray = [
            ...innerArray.map((c) => this._contactBuilder.buildContact(c)),
          ];
          const memberUpdates: any[] = [];
          contactArray.forEach((c: any) => {
            const member = this.contactList()!.updaters.filter(
              (m) => m.contactId === c.id
            );
            if (member.length > 0) {
              const newMember = Object.assign({}, member[0]);
              newMember.label = c.firstName + ' ' + c.lastName;
              memberUpdates.push(Object.assign({}, newMember));
            }
          });
          this.contactList()!.updaters = [...memberUpdates];
          this.contactList.set({ ...this.contactList()! });
          this.memberContactArray = this.contactList()?.members.map(
            (member) => member.contact!
          )!;
          this.loaded = true;
        },
      });
  }

  facilitySearch($event: AutoCompleteCompleteEvent, type: string): void {
    this.filteredFacilities = [...this.facilities].sort(dynamicSort(type, 1));
    if (
      !isNullOrUndefined($event) &&
      !isNullOrUndefined($event.query) &&
      !isNullOrUndefined(type)
    ) {
      this.filteredFacilities = this.filteredFacilities.filter((f) => {
        if (!isNullOrUndefined(f[type])) {
          return (
            f[type].toLowerCase().includes($event.query.toLowerCase()) &&
            f[type].trim() !== ''
          );
        }
      });
    }

    const state = this.addToFacilityForm.controls['state'].value;
    const county = this.addToFacilityForm.controls['county'].value
      ? this.addToFacilityForm.controls['county'].value.county
      : null;
    const township = this.addToFacilityForm.controls['township'].value
      ? this.addToFacilityForm.controls['township'].value.township
      : null;
    this.filteredFacilities = this.filteredFacilities.filter((f) => {
      let isValid = true;
      if (!isNullOrEmptyString(state) && !f.state.includes(state)) {
        isValid = false;
      }
      if (!isNullOrEmptyString(county) && !f.county.includes(county)) {
        isValid = false;
      }
      if (!isNullOrEmptyString(township) && !f.township.includes(township)) {
        isValid = false;
      }

      return isValid;
    });
  }

  selectFacility($event: AutoCompleteSelectEvent): void {
    const interior = $event.value;
    this.addToFacilityForm.patchValue({
      facility: interior,
      state: interior.state,
      county: { county: interior.county, state: interior.state },
      township: { township: interior.township },
    });
  }

  clickedSchedule(): void {
    // if we are changing tabs we need to resize the calendar because otherwise its rendering appears to break
    // seems like an issue with the fullcalendar component but this looks to solve it
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 1);
  }

  loadFacilities(): void {
    this._facilityApi
      .getFacilities()
      .pipe(take(1))
      .subscribe((data) => {
        this.facilities = data;
        this.facilities = this.facilities.filter(
          (facility) => facility.hidden == false
        );
      });
  }

  async addContactListToFacility(): Promise<void> {
    this.saving = true;
    const raw = this.addToFacilityForm.getRawValue();
    const facilityContactListType =
      raw.facilityContactListType as FacilityContactListType;
    const facility = this.facilities.find((f) => f.id === raw.facility.id);
    const facilityLists = await firstValueFrom(
      this._contactListApi.getFacilityContactLists(raw.facility.id)
    );

    const confHeader$ = this._translateService.get(
      'CONTACT.MESSAGES.HEADERS.OVERWRITE_CONTACT_LIST'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');

    if (
      facilityLists.ContactList &&
      facilityContactListType === 'contactList'
    ) {
      const message$ = this._translateService.get(
        'CONTACT.MESSAGES.CONFIRMATION.OVERWRITE_CONTACT_LIST',
        {
          contactList: facilityLists.ContactList.name,
          facility: facility.facilityName,
        }
      );

      let [confHeader, ok, cancel, message] = await firstValueFrom(
        forkJoin([confHeader$, ok$, cancel$, message$])
      );

      this._confirmationService.confirm({
        header: confHeader,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok,
        rejectLabel: cancel,
        message: message,
        accept: () => {
          this.addContactListToFacilityInternal(
            facility,
            facilityContactListType
          );
        },
        reject: () => {
          this.saving = false;
        },
      });
    } else if (
      facilityLists.ManagementContactList &&
      facilityContactListType === 'managementContactList'
    ) {
      const message$ = this._translateService.get(
        'CONTACT.MESSAGES.CONFIRMATION.OVERWRITE_CONTACT_LIST',
        {
          contactList: facilityLists.ManagementContactList.name,
          facility: facility.facilityName,
        }
      );

      let [confHeader, ok, cancel, message] = await firstValueFrom(
        forkJoin([confHeader$, ok$, cancel$, message$])
      );

      this._confirmationService.confirm({
        header: confHeader,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok,
        rejectLabel: cancel,
        message: message,
        accept: () => {
          this.addContactListToFacilityInternal(
            facility,
            facilityContactListType
          );
        },
        reject: () => {
          this.saving = false;
        },
      });
    } else if (
      facilityContactListType === 'additionalResourceList' &&
      facilityLists.AdditionalResourceLists.some((farl) => {
        farl.AdditionalResourceListType.id ===
          this.contactList()?.AdditionalResourceListType.id;
      })
    ) {
      const message$ = this._translateService.get(
        'CONTACT.MESSAGES.CONFIRMATION.OVERWRITE_CONTACT_LIST',
        {
          contactList: facilityLists.AdditionalResourceLists.find((farl) => {
            farl.AdditionalResourceListType.id ===
              this.contactList()?.AdditionalResourceListType.id;
          })!.name,
          facility: facility.facilityName,
        }
      );

      let [confHeader, ok, cancel, message] = await firstValueFrom(
        forkJoin([confHeader$, ok$, cancel$, message$])
      );

      this._confirmationService.confirm({
        header: confHeader,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok,
        rejectLabel: cancel,
        message: message,
        accept: () => {
          this.addContactListToFacilityInternal(
            facility,
            facilityContactListType
          );
        },
        reject: () => {
          this.saving = false;
        },
      });
    } else {
      this.addContactListToFacilityInternal(facility, facilityContactListType);
    }
  }

  addContactListToFacilityInternal(
    facility: Facility | null | undefined,
    facilityContactListType: FacilityContactListType
  ): void {
    if (facility) {
      this._facilityApi
        .addContactListToFacility(
          facility.id,
          this.contactList()!.id,
          facilityContactListType
        )
        .subscribe({
          next: (data: Facility) => {
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
            });
            this.saving = false;
            let newFacilities = [...this.contactList()?.facilities!];
            let newManagementFacilities = [
              ...this.contactList()?.ManagementFacilities!,
            ];
            let newAdditionalResourceFacilities = [
              ...this.contactList()?.AdditionalResourceListFacilities!,
            ];

            if (facilityContactListType === 'contactList') {
              newFacilities = [...newFacilities, data];
            } else if (facilityContactListType === 'managementContactList') {
              newManagementFacilities = [...newManagementFacilities, data];
            } else if (facilityContactListType === 'additionalResourceList') {
              newAdditionalResourceFacilities = [
                ...newAdditionalResourceFacilities,
                data,
              ];
            }

            this.contactList.set({
              ...this.contactList()!,
              facilities: newFacilities,
              ManagementFacilities: newManagementFacilities,
              AdditionalResourceListFacilities: newAdditionalResourceFacilities,
            });
            this.addToFacilityForm.reset();
            this.addToFacilityForm.patchValue({
              facilityContactListType: 'contactList',
            });
            this.addToFacilityForm.markAsPristine();
          },
          error: (error) => {
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'CONTACT.MESSAGES.ERROR.ADD_CONTACT_LIST_TO_FACILITY',
            });
            console.error(error);
            this.saving = false;
            this.addToFacilityForm.reset();
          },
        });
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'CONTACT.MESSAGES.ERROR.ADD_CONTACT_LIST_TO_FACILITY',
      });
    }
  }

  deleteContactListFromFacility(
    $event: string,
    facilityListToUpdate: FacilityContactListType
  ): void {
    this.saving = true;
    const facility = this.facilities.find((f) => f.id === $event);
    if (facility) {
      this._facilityApi
        .deleteContactListFromFacility(facility.id, this.contactList()!.id)
        .subscribe({
          next: (data: Facility) => {
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
            });

            const contactList = this.contactList();
            let newFacilities = contactList?.facilities ?? [];
            let newManagementFacilities =
              contactList?.ManagementFacilities ?? [];
            let newAdditionalResourceListFacilities =
              contactList?.AdditionalResourceListFacilities ?? [];

            if (facilityListToUpdate === 'contactList') {
              newFacilities = newFacilities.filter(
                (fac) => fac.id !== facility.id
              );
            } else if (facilityListToUpdate === 'managementContactList') {
              newManagementFacilities = newManagementFacilities.filter(
                (fac) => fac.id !== facility.id
              );
            } else if (facilityListToUpdate === 'additionalResourceList') {
              newAdditionalResourceListFacilities =
                newAdditionalResourceListFacilities.filter(
                  (fac) => fac.id !== facility.id
                );
            }

            this.contactList.set({
              ...this.contactList()!,
              facilities: newFacilities,
              ManagementFacilities: newManagementFacilities,
              AdditionalResourceListFacilities:
                newAdditionalResourceListFacilities,
            });
            this.saving = false;
          },
          error: (error) => {
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey:
                'CONTACT.MESSAGES.ERROR.DELETE_CONTACT_LIST_FROM_FACILITY',
            });
            console.error(error);
            this.saving = false;
          },
        });
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        bodyKey: 'CONTACT.MESSAGES.ERROR.DELETE_CONTACT_LIST_FROM_FACILITY',
      });
    }
  }

  openFacility(fac: Facility): void {
    if (fac && fac.id) {
      const tab = this._tabService.buildNewTab(
        FACILITY_DETAIL_CONTAINER,
        true,
        undefined,
        fac.id
      );
      if (this._deviceService.isMobile()) {
        this._tabService.setMobileTab(tab);
      } else {
        this._tabService.openTab(tab);
      }
    }
  }

  contactSearch($event: AutoCompleteCompleteEvent): void {
    if ($event.query) {
      this.filteredContacts = this.allContacts
        .filter((c) =>
          (c.firstName + ' ' + c.lastName)
            .toLowerCase()
            .includes($event.query.toLowerCase())
        )
        .filter((c: Contact) => c.inactiveAdAccount === false)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }))
        .sort(dynamicSort('label', 1));
    } else {
      this.filteredContacts = this.allContacts
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }))
        .sort(dynamicSort('label', 1));
    }
  }

  addContactAsUpdater(): void {
    if (this.selectedUpdater) {
      const fullContact = this.allContacts.find(
        (c) => c.id == this.selectedUpdater
      )!;
      const updaterData = {
        label: fullContact.firstName + ' ' + fullContact.lastName,
        value: fullContact.id,
      };
      this.contactList()!.updaters = [
        ...this.contactList()!.updaters,
        this._contactListBuilder.buildNewUpdater(
          this.contactList()!.id,
          updaterData
        ),
      ];
      this.contactList.set({ ...this.contactList()! });
      this.memberContactArray = this.contactList()?.members.map(
        (member) => member.contact!
      )!;
      const view = this.view();
      if (view) {
        this.saving = true;
        setTimeout(() => {
          view.saveContactList();
        }, 1);
      }
    }
    this.selectedUpdater = null;
    this.saving = false;
  }

  async removeContactAsUpdater(id: string): Promise<void> {
    if (!id) {
      console.error(
        'Attempted to remove contact as updater without supplying id!'
      );
      return;
    }

    const translations = await firstValueFrom(
      forkJoin({
        header: this._translateService.get(
          'CONTACT.MESSAGES.HEADERS.REMOVE_UPDATER'
        ),
        message: this._translateService.get(
          'CONTACT.MESSAGES.CONFIRMATION.REMOVE_UPDATER'
        ),
      })
    );

    this._confirmationService.confirm({
      header: translations.header,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this.okBtnLabel,
      rejectLabel: this.cancelBtnLabel,
      message: translations.message,
      accept: () => {
        const idx = this.contactList()!.updaters.findIndex((u) => u.id === id);
        this.contactList()!.updaters.splice(idx, 1);
        this.contactList.set({ ...this.contactList()! });
        const view = this.view();
        if (view) {
          this.saving = true;
          setTimeout(() => {
            view.saveContactList();
          }, 1);
        }
        this.saving = false;
      },
      reject: () => {},
    });
  }

  updateActionsList(): void {
    const selectAction$ = this._translateService.get(
      'COMMON.LABEL.SELECT_ACTION'
    );
    const viewAudit$ = this._translateService.get(
      'COMMON.LABEL.VIEW_AUDIT_INFO'
    );
    const deleteMsg$ = this._translateService.get(
      'CONTACT.LABEL.DELETE_CONTACT_LIST'
    );

    let select,
      audit,
      deleteMsg = null;
    forkJoin([selectAction$, viewAudit$, deleteMsg$])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        audit = messages[1];
        deleteMsg = messages[2];
        this.actions = [
          { label: select, value: null },
          { label: audit, value: VIEW_AUDIT },
        ];

        if (this.canDelete) {
          this.actions.push({ label: deleteMsg, value: DELETE_LIST });
        }
        // default actions here
        this.actions = [...this.actions];
        this.selectedAction = null;
      });
  }

  actionEvent($event: SelectChangeEvent, object?: Select): void {
    if ($event.value) {
      this.selectedAction = null;
      if ($event.value) {
        if ($event.value === VIEW_AUDIT) {
          const history$ = this._contactListApi.getContactListHistories(
            this.contactList()!.id
          );
          this._dialogService.open(AuditDialogContainerComponent, {
            dismissableMask: true,
            modal: true,
            closable: true,
            width: '25%',
            data: {
              history: history$,
            },
          });
        }

        if ($event.value === DELETE_LIST) {
          this.deleteContactList();
        }
      }

      if (object) {
        object.clear();
      }
    }
  }

  deleteContactList(): void {
    const deleteEntryMsg$ = this._translateService.get(
      'CONTACT.MESSAGES.CONFIRMATION.DELETE_CONTACT_LIST'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
    const deleteEntryHeader$ = this._translateService.get(
      'CONTACT.MESSAGES.HEADERS.DELETE_CONTACT_LIST'
    );
    forkJoin([ok$, cancel$, deleteEntryMsg$, deleteEntryHeader$]).subscribe(
      (messages) => {
        this.okBtnLabel = messages[0];
        this.cancelBtnLabel = messages[1];
        this.deleteEntryMsg = messages[2];
        this.deleteEntryHeader = messages[3];
      }
    );

    this._confirmationService.confirm({
      header: this.deleteEntryHeader,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: this.okBtnLabel,
      rejectLabel: this.cancelBtnLabel,
      message: this.deleteEntryMsg,
      accept: () => {
        const id = this.contactList()!.id;
        this.saving = true;
        this._contactListApi.deleteContactList(id).subscribe({
          next: (result) => {
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
            });
            this.saving = false;
          },
          error: (error) => {
            this.saving = false;
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.DELETE_ERROR',
              bodyKey: 'COMMON.MESSAGES.ERROR.DELETE_ERROR_MSG',
            });
          },
          complete: () => {
            if (this._deviceService.isMobile()) {
              const tab = this._tabService.buildNewTab(
                CONTACT_LIST_SEARCH_CONTAINER,
                true
              );
              this._tabService.setMobileTab(tab);
            } else {
              const index = this._tabService.getActiveIndex();
              this._tabService.closeTab(index);
            }
          },
        });
      },
      reject: () => {},
    });
  }

  loadCalendarEvents(): void {
    this.loading = true;
    this._contactListApi
      .getContactListSchedules(this.contactList()!.id)
      .pipe()
      .subscribe((schedules) => {
        this.events = [];
        this.contactListSchedule = [...schedules];
        this.contactListSchedule.forEach((s) => {
          let timeConvertedSchedule =
            this._contactListBuilder.buildContactListSchedule(s);
          this.events.push({
            title:
              (s.isPrimary ? 'Primary: ' : 'Secondary: ') +
              s.contact?.firstName +
              ' ' +
              s.contact?.lastName,
            start: timeConvertedSchedule.startTime,
            end: timeConvertedSchedule.endTime,
            utcStart: timeConvertedSchedule.utcStartTime,
            utcEnd: timeConvertedSchedule.utcEndTime,
            ...s,
          });
        });
        this.calendarOptions.events = this.events;
        this.loading = false;
      });
  }

  handleDateClick(arg: DateClickInfo): void {
    if (this.canSchedule) {
      const startDate = new Date(arg.dateStr + ' ');
      const todayClone = this.clearTimeFromDate(this.today);

      if (startDate >= todayClone) {
        this.scheduleFormVisible = true;
        this.onDateSelected(arg);
      } else {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CONTACT.MESSAGES.HEADERS.INVALID_DATES',
          bodyKey: 'CONTACT.MESSAGES.WARN.INVALID_DATE_SELECTION',
        });
      }
    }
  }

  handleEventClick($event: EventClickInfo): void {
    if (this.canSchedule) {
      this.onEventSelected($event);
      this.scheduleFormVisible = true;
    }
  }

  handleSelectable(arg: DateSelectInfo): void {
    if (this.canSchedule) {
      const startDate = new Date(arg.start);
      const endDate = new Date(arg.end);
      endDate.setDate(endDate.getDate() - 1);
      const todayClone = this.clearTimeFromDate(this.today);

      if (startDate.toDateString() === endDate.toDateString()) {
        return;
      }

      if (startDate >= todayClone) {
        this.scheduleFormVisible = true;
        this.onDatesSelected(arg);
      } else {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CONTACT.MESSAGES.HEADERS.INVALID_DATES',
          bodyKey: 'CONTACT.MESSAGES.WARN.INVALID_DATE_SELECTION',
        });
      }
    }
  }

  clearTimeFromDate(datetime: Date): Date {
    let clone = new Date(datetime);
    clone.setHours(0);
    clone.setMinutes(0);
    clone.setSeconds(0);
    clone.setMilliseconds(0);
    return clone;
  }

  onScheduleFormHidden(): void {
    this.scheduleFormVisible = false;
  }

  onDateSelected(arg: any): void {
    this.scheduleFormType = 'Create';
    const scheduleForm = this.scheduleForm();
    scheduleForm.disableDelete = true;
    const startTime = new Date(arg.dateStr + ' ');
    startTime.setHours(7);
    const endTime = new Date(arg.dateStr + ' ');
    endTime.setDate(endTime.getDate() + 1);
    endTime.setHours(7);

    scheduleForm.scheduleFormGroup.patchValue({
      startTime: new Date(startTime),
      endTime: new Date(endTime),
    });
  }

  onDatesSelected(arg: any): void {
    this.scheduleFormType = 'Create';
    const scheduleForm = this.scheduleForm();
    scheduleForm.disableDelete = true;
    const startTime = new Date(arg.start + ' ');
    startTime.setHours(7);
    const endTime = new Date(arg.end + ' ');
    endTime.setDate(endTime.getDate() - 1);
    endTime.setHours(7);

    scheduleForm.scheduleFormGroup.patchValue({
      startTime: new Date(startTime),
      endTime: new Date(endTime),
    });
  }

  onEventSelected($event: any): void {
    this.selectedSchedule = $event.event;
    this.scheduleFormType = 'Edit';
    const todayClone = this.today;

    let scheduleTimezone = this.selectedSchedule.extendedProps.timezone;

    const scheduleForm = this.scheduleForm();
    if (this.selectedSchedule.end < todayClone) {
      this.scheduleForm().scheduleFormGroup.disable();
      scheduleForm.disableDelete = true;
    }

    if (this.selectedSchedule.start <= todayClone) {
      scheduleForm.disableDelete = true;
      scheduleForm.scheduleFormGroup.get('startTime')?.disable();
    }

    scheduleForm.scheduleFormGroup.patchValue({
      startTime: getDateInTimeZone(
        this.selectedSchedule.extendedProps.utcStart,
        scheduleTimezone
      ),
      endTime: getDateInTimeZone(
        this.selectedSchedule.extendedProps.utcEnd,
        scheduleTimezone
      ),
      scheduleTimeZone: scheduleForm.timeZones.find((tz) =>
        tz.value?.includes(scheduleTimezone)
      ),
      version: this.selectedSchedule.extendedProps.version,
    });
    if (this.selectedSchedule.extendedProps.isPrimary) {
      scheduleForm.scheduleFormGroup.get('secondary')?.disable();
      scheduleForm.scheduleFormGroup.patchValue({
        primary: scheduleForm.contacts.find(
          (c) =>
            c.firstName ===
              this.selectedSchedule.extendedProps.contact.firstName &&
            c.lastName ===
              this.selectedSchedule.extendedProps.contact.lastName &&
            c.id === this.selectedSchedule.extendedProps.contact.id
        ),
      });
    } else {
      scheduleForm.scheduleFormGroup.get('primary')?.disable();
      scheduleForm.scheduleFormGroup.patchValue({
        secondary: scheduleForm.contacts.find(
          (c) =>
            c.firstName ===
              this.selectedSchedule.extendedProps.contact.firstName &&
            c.lastName === this.selectedSchedule.extendedProps.contact.lastName
        ),
      });
    }
  }

  onScheduleFormSubmitted($event: any): void {
    let eventsCopy = [...this.events];
    let newEvent = {
      title:
        ($event.isPrimary ? 'Primary: ' : 'Secondary: ') +
        $event.contact.firstName +
        ' ' +
        $event.contact.lastName,
      start: $event.actualStartTime,
      end: $event.actualEndTime,
      ...$event,
    };

    delete newEvent.startTime;
    delete newEvent.endTime;

    eventsCopy.push(newEvent);
    this.events = eventsCopy;
    this.loadCalendarEvents();
  }

  onScheduleDeleted($event: any): void {
    let newEvents = this.events.filter((e) => e.id !== $event);

    this.events = newEvents;
    this.calendarOptions.events = this.events;
  }

  onScheduleImported(): void {
    this.loadCalendarEvents();
  }

  onScheduleExport(): void {
    this.exportSchedulesToCsv();
  }

  exportSchedulesToCsv(): void {
    let emptyScheduleData = {
      'User adAccountName': '',
      'Primary or Secondary': '',
      'Start Time': new Date(),
      'End Time': new Date(),
      'Time Zone': '',
    };
    let exportScheduleData = this.events
      .map((e) => ({
        'User adAccountName': e.contact.adAccountName,
        'Primary or Secondary': e.isPrimary ? 'P' : 'S',
        'Start Time': e.start,
        'End Time': e.end,
        'Time Zone': e.timezone,
      }))
      .sort((a, b) => sortByProperty(a, b, 'Start Time'))
      .filter((s) => s['Start Time'] > this.today);

    const delimiter = ',';
    const keys = Object.keys(exportScheduleData[0] || emptyScheduleData);
    const csvContent =
      keys.join(delimiter) +
      '\n' +
      exportScheduleData
        .map((row: any) => {
          return keys
            .map((k) => {
              let cell = row[k] === null || row[k] === undefined ? '' : row[k];
              cell =
                cell instanceof Date
                  ? moment(cell).format('YYYY-MM-DDTHH:mm:ss')
                  : cell.toString().replace(/"/g, '""');
              if (cell.search(/("|,|\n)/g) >= 0) {
                cell = `"${cell}"`;
              }
              return cell;
            })
            .join(delimiter);
        })
        .join('\n');

    const blob = new Blob([csvContent], { type: 'csv; charset=utf-8;' });
    const link = document.createElement('a');
    const downloadURL = window.URL.createObjectURL(blob);
    link.href = downloadURL;
    link.download = this.contactList()!.name + '/ContactList/Schedules.csv';
    link.click();
  }

  filterFacilities($event: KeyboardEvent): void {
    this.facilityFilterText.set(($event.target as HTMLInputElement).value);
  }

  setupFacilityHeaders(): void {
    const translateObservables: Array<Observable<any>> = [];
    translateObservables.push(
      this._translateService.get('CONTACT.LABEL.CONTACT_LIST_FACILITIES')
    );
    translateObservables.push(
      this._translateService.get(
        'CONTACT.LABEL.MANAGEMENT_CONTACT_LIST_FACILITIES'
      )
    );
    translateObservables.push(
      this._translateService.get(
        'CONTACT.LABEL.ADDITIONAL_RESOURCE_LIST_FACILITIES'
      )
    );

    forkJoin(translateObservables).subscribe({
      next: (value: any[]) => {
        this.clFacilitiesHeader.set(value[0]);
        this.managementFacilitiesHeader.set(value[1]);
        this.additionalResourceListFacilitiesHeader.set(value[2]);
      },
    });
  }
}
